import type { ReactNode } from "react";
import { ClerkProvider } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  // Fail loudly at boot so a missing key isn't silently a "Clerk widget
  // doesn't render" head-scratcher in production. The dev server picks up
  // .env.local on restart, so this is recoverable in development too.
  console.error(
    "VITE_CLERK_PUBLISHABLE_KEY is not set — admin auth will not work. Set it in .env.local.",
  );
}

/**
 * `<ClerkProvider>` wired to React Router.
 *
 * Clerk's prebuilt widgets use **path-based routing** (`<SignIn path="/login"
 * routing="path">`, `<SignUp path="/invite-accept" …>`): each step of a flow
 * lives at its own URL and Clerk navigates between them itself. Which router
 * it uses for that is our choice — and if we don't give it one, it falls back
 * to a **hard browser navigation**, reloading the whole SPA on every step.
 *
 * That was visible as: enter the emailed code → the password step appears →
 * the page immediately reloads and shows the password step again. The reload
 * threw away the mounted app (and any in-flight state) mid-signup.
 *
 * `routerPush`/`routerReplace` hand Clerk React Router's `navigate`, so step
 * transitions become ordinary client-side navigations. Clerk's types require
 * **both or neither** — passing one alone is a type error, not a silent
 * half-fix.
 *
 * Because it needs `useNavigate()`, this component must render **inside**
 * `<BrowserRouter>` — which is why the provider lives here rather than in
 * `main.tsx`.
 */
export function ClerkWithRouter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY ?? ""}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      // After sign-out, send the user to the login route. Clerk's default
      // is `/` which would just bounce them through any protected-route
      // guard back to login — extra render pass for nothing.
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}
